<template>
  <div>
    <!-- 加载状态 -->
    <div v-if="isLoading" class="flex justify-center items-center py-20">
      <div class="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
    </div>
    
    <!-- 音乐列表 -->
    <template v-else-if="musicList.length > 0">
      <!-- PC端布局 -->
      <div class="hidden sm:grid sm:grid-cols-2 lg:grid-cols-6 md:grid-cols-4 gap-6">
        <div
          v-for="music in musicList"
          :key="music.id"
          class="music-item group rounded-xl overflow-hidden shadow-md hover:shadow-lg transition-all hover:scale-[1.02] hover:-translate-y-1 duration-300 group cursor-pointer"
          role="link"
          tabindex="0"
          @click="handleMusicCardClick(music, 'card')"
          @keydown.enter.prevent="handleMusicCardClick(music, 'card_keyboard')"
        >
          <!-- 封面图 -->
          <div class="relative aspect-square overflow-hidden group">
            <NuxtLink :to="`/music/song/${music.id}`" class="block w-full h-full" @click.stop="trackMusicCardClick(music, 'cover')">
              <CloudflareImg
                :src="music.image_url"
                :alt="music.title"
                rounded="xl"
                class="transition-transform duration-300 group-hover:scale-110"
              />
            </NuxtLink>

            <!-- 播放按钮 -->
            <div
              class="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 cursor-pointer z-10"
              @click.stop="handleTogglePlayState(music)"
            >
              <div class="relative z-10">
                <div v-if="!isPlaying(music)" class="w-8 h-8 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 flex items-center justify-center shadow-lg">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-white" viewBox="0 0 24 24" fill="white">
                    <path d="M8 5v14l11-7z" />
                  </svg>
                </div>
                <div v-else class="w-8 h-8 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 flex items-center justify-center shadow-lg">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-white" viewBox="0 0 24 24" fill="white">
                    <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" />
                  </svg>
                </div>
              </div>
            </div>
            <!-- 正在播放指示器 -->
            <div
              v-if="isPlaying(music)"
              class="absolute inset-0 flex items-center justify-center z-20 group-hover:hidden"
            >
              <div class="w-14 h-14 rounded-full bg-black/50 backdrop-blur-sm flex items-center justify-center">
                <div class="sound-wave">
                  <span></span>
                  <span></span>
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
              <div class="absolute bottom-0 left-0 right-0 h-1 bg-blue-500">
                <div class="sound-wave-bar w-full h-full"></div>
              </div>
            </div>
            <button
              v-if="showCreateSimilar"
              type="button"
              @click.stop="handleCreateSimilarMusic(music)"
              class="absolute bottom-2 left-1/2 -translate-x-1/2 text-center rounded-full w-[70%] max-w-[100px] py-2 bg-[rgba(255,255,255,0.3)] text-white text-xs z-20 cursor-pointer opacity-0 group-hover:opacity-100 transition-opacity duration-300"
            >
              {{ t('music.createSimilar') }}
            </button>
          </div>

          <!-- 音乐信息 -->
          <NuxtLink :to="`/music/song/${music.id}`" class="block p-4 relative" @click.stop="trackMusicCardClick(music, 'text')">
            <div class="absolute inset-0 bg-gradient-to-l from-blue-500/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            <div class="relative z-10">
              <h3 class="font-bold text-white truncate hover:text-blue-300" :title="music.title">{{ music.title || music.metadata?.prompt }}</h3>
              <p class="text-gray-400 text-sm mb-2 h-5 truncate overflow-hidden whitespace-nowrap">
                {{ music.metadata && music.metadata.tags ? music.metadata.tags.split(',')[0] : '' }}
              </p>
              <p class="truncate text-gray-300 rounded-full py-1 text-xs max-w-full overflow-hidden whitespace-nowrap" :title="music.metadata?.tags">
                {{ music.metadata && music.metadata.tags ? music.metadata.tags : '' }}
              </p>
            </div>
          </NuxtLink>
        </div>
      </div>
      
      <!-- 移动端布局（左右结构） -->
      <div class="sm:hidden">
        <div
          v-for="music in musicList"
          :key="music.id"
          class="flex items-center py-3 hover:bg-gray-700/30 cursor-pointer"
          role="link"
          tabindex="0"
          @click="handleMusicCardClick(music, 'mobile_row')"
          @keydown.enter.prevent="handleMusicCardClick(music, 'mobile_row_keyboard')"
        >
          <!-- 封面 -->
          <NuxtLink :to="`/music/song/${music.id}`" class="w-16 h-16 relative rounded-md overflow-hidden mr-3 shrink-0 block" @click.stop="trackMusicCardClick(music, 'mobile_cover')">
            <CloudflareImg
              :src="music.image_url"
              :alt="music.title"
              width="64"
              height="64"
              quality="80"
              rounded="md"
            />

            <!-- 播放状态指示器 -->
            <div v-if="isPlaying(music)" class="absolute inset-0 flex items-center justify-center bg-black/40 z-20">
              <div class="sound-wave-small">
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>

            <!-- 时长 -->
            <div class="absolute bottom-1 right-1 px-1.5 py-0.5 bg-black/60 rounded text-white text-[10px]">
              {{ formatDuration(music.metadata?.duration) }}
            </div>
          </NuxtLink>

          <!-- 音乐信息 -->
          <NuxtLink :to="`/music/song/${music.id}`" class="flex-1 min-w-0 mr-3 block" @click.stop="trackMusicCardClick(music, 'mobile_text')">
            <h4 class="font-medium text-white truncate" :title="music.title">{{ music.title || music.metadata?.prompt }}</h4>
            <p class="text-gray-400 text-xs truncate overflow-hidden whitespace-nowrap">
              {{ music.metadata && music.metadata.tags ? music.metadata.tags.split(',')[0] : '' }}
            </p>
            <p class="truncate text-gray-300 text-xs max-w-full mt-1 overflow-hidden whitespace-nowrap" :title="music.metadata?.tags">
              {{ music.metadata && music.metadata.tags ? music.metadata.tags : '' }}
            </p>
          </NuxtLink>
          <div class="flex flex-col items-center gap-2 lg:gap-4">
            <!-- 播放按钮 -->
            <button
              @click.stop="handleTogglePlayState(music)"
              :aria-label="!isPlaying(music) ? 'Play ' + music.title : 'Pause ' + music.title"
              class="w-8 h-8 rounded-full flex items-center justify-center bg-gray-700/50 hover:bg-blue-600/50"
            >
              <svg v-if="isPlaying(music)" xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-white" viewBox="0 0 24 24" fill="currentColor">
                <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" />
              </svg>
              <svg v-else xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-white" viewBox="0 0 24 24" fill="currentColor">
                <path d="M8 5v14l11-7z" />
              </svg>
            </button>
            <button
              v-if="showCreateSimilar"
              type="button"
              @click.stop="handleCreateSimilarMusic(music)"
              class="text-center rounded-full px-4 py-2 bg-gray-700/50 text-white text-xs"
            >
              {{ t('music.createSimilar') }}
            </button>
          </div>
        </div>
      </div>
    </template>
    
    <!-- 无结果 -->
    <div v-else-if="!isLoading && musicList.length === 0" class="flex flex-col items-center justify-center py-20">
      <p class="text-gray-500">{{ t('music.noResults') }}</p>
    </div>

    <!-- 加载更多触发器（仅 autoLoadMore 模式） -->
    <div
      v-if="musicList.length > 0 && autoLoadMore !== false"
      ref="loadMoreTrigger"
      class="py-10 flex flex-col items-center justify-center mt-4"
      style="min-height: 100px; position: relative; z-index: 1;"
    >
      <div v-if="isLoadingMore" class="flex items-center space-x-3">
        <div class="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-blue-500"></div>
        <span class="text-gray-400 text-sm">{{ t('music.loading') }}</span>
      </div>
      <div v-else-if="!hasMore" class="text-gray-400 text-sm">
        {{ t('music.noMoreResults') }}
      </div>
    </div>

    <!-- 错误信息 -->
    <div v-if="error" class="flex flex-col items-center justify-center py-10">
      <p class="text-red-500 mb-4">{{ error }}</p>
      <button @click="reload" :loading="isLoading">{{ t('common.reload') }}</button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, nextTick, watch } from 'vue'
import { useI18n, useRouter } from '#imports'
import type { Music } from '~/services/musicService'
import CloudflareImg from '~/components/base/CloudflareImg.vue'
import { useMusicCreate } from '~/composables/useMusicCreate'
import { useToast } from '~/composables/useToast'
import { trackEvent } from '~/utils/analytics'

// 获取国际化函数
const { t } = useI18n()

// 使用音乐交互的composable
const useMusicInteraction = () => {
  const router = useRouter()
  const toast = useToast()
  const { saveMusicData } = useMusicCreate()
  const { t } = useI18n()

  // 播放状态
  const currentPlayingId = ref<string | null>(null)
  const isCurrentlyPlaying = ref(false)

  // 播放音乐
  const playMusic = (music: Music) => {
    console.log('开始播放音乐', {
      musicId: music.id,
      title: music.title
    })
    currentPlayingId.value = music.id
    isCurrentlyPlaying.value = true
    // 触发全局播放事件
    const event = new CustomEvent('play-music', { detail: music })
    window.dispatchEvent(event)
  }

  // 切换播放状态
  const togglePlayState = (music: Music) => {
    console.log('切换播放状态', {
      currentPlayingId: currentPlayingId.value,
      musicId: music.id,
      isCurrentlyPlaying: isCurrentlyPlaying.value
    })
    
    if (currentPlayingId.value === music.id) {
      // 如果是当前正在播放的歌曲，则切换播放/暂停状态
      isCurrentlyPlaying.value = !isCurrentlyPlaying.value
      console.log('切换当前歌曲的播放状态', { isPlaying: isCurrentlyPlaying.value })
      // 触发全局播放状态切换事件
      const event = new CustomEvent('toggle-play-state', {
        detail: { id: music.id, isPlaying: isCurrentlyPlaying.value }
      })
      window.dispatchEvent(event)
    } else {
      // 如果是新的歌曲，则开始播放
      playMusic(music)
    }
  }

  // 检查是否正在播放
  const isPlaying = (music: Music) => {
    const playing = currentPlayingId.value === music.id && isCurrentlyPlaying.value
    return playing
  }

  // 导航到音乐详情
  const navigateToMusicDetail = (music: Music) => {
    router.push(`/music/song/${music.id}`)
  }

  // 创建同款音乐
  const createSimilarMusic = (music: Music) => {
    if (!music) return
    try {
      // 从音乐元数据中提取歌词信息或使用描述作为歌词
      const lyricsContent = music.metadata?.prompt || ''
      const musicTitle = music.title || ''
      // 确保标签是字符串格式
      const tagsString = typeof music.metadata?.tags === 'string' ? 
                        music.metadata.tags : 
                        (music.metadata?.tags ? String(music.metadata.tags) : '')
      
      // 准备做同款数据
      const similarData = {
        prompt: music.metadata?.prompt || '',
        tags: tagsString,
        instrumental: false,
        lyrics: lyricsContent,
        title: musicTitle,
        mode: 'advanced'
      }
      
      // 保存音乐数据并导航
      saveMusicData(similarData, router.currentRoute.value.path)
      router.push('/create')
    } catch (error) {
      toast.error(t('common.operation_failed'))
    }
  }

  // 监听播放状态变化
  const setupPlayingStateListener = () => {
    const handler = ((e: CustomEvent) => {
      console.log('收到播放状态变化事件', e.detail)
      const { id, isPlaying } = e.detail
      currentPlayingId.value = id
      isCurrentlyPlaying.value = isPlaying
    }) as EventListener

    window.addEventListener('playing-state-changed', handler)
    return () => {
      window.removeEventListener('playing-state-changed', handler)
    }
  }

  return {
    currentPlayingId,
    isCurrentlyPlaying,
    playMusic,
    togglePlayState,
    isPlaying,
    navigateToMusicDetail,
    createSimilarMusic,
    setupPlayingStateListener
  }
}

const props = defineProps<{
  musicList: Music[]
  isLoading: boolean
  isLoadingMore: boolean
  hasMore: boolean
  error: string | null
  showCreateSimilar?: boolean
  autoLoadMore?: boolean
  source?: string
}>()

const emit = defineEmits<{
  (e: 'reload'): void
  (e: 'loadMore'): void
}>()

// 使用音乐交互逻辑
const {
  isPlaying,
  togglePlayState,
  navigateToMusicDetail,
  createSimilarMusic,
  setupPlayingStateListener
} = useMusicInteraction()

const getMusicTrackParams = (music: Music, actionArea: string) => ({
  source: props.source || 'music_list',
  action_area: actionArea,
  song_id: music.id,
  title: music.title || '',
  has_audio: Boolean(music.audio_url),
  has_image: Boolean(music.image_url)
})

const trackMusicCardClick = (music: Music, actionArea: string) => {
  trackEvent('music_card_click', getMusicTrackParams(music, actionArea))
}

const handleMusicCardClick = (music: Music, actionArea: string) => {
  trackMusicCardClick(music, actionArea)
  navigateToMusicDetail(music)
}

const handleTogglePlayState = (music: Music) => {
  trackEvent('music_play_click', {
    ...getMusicTrackParams(music, 'play_button'),
    will_play: !isPlaying(music)
  })
  togglePlayState(music)
}

const handleCreateSimilarMusic = (music: Music) => {
  trackEvent('create_similar_click', getMusicTrackParams(music, 'create_similar'))
  createSimilarMusic(music)
}

// 加载更多触发器
const loadMoreTrigger = ref<HTMLElement | null>(null)
const observer = ref<IntersectionObserver | null>(null)
const loadMoreCooldown = ref(false)

// 处理加载更多
const handleLoadMore = () => {
  if (!props.isLoading && !props.isLoadingMore && props.hasMore && !loadMoreCooldown.value) {
    loadMoreCooldown.value = true
    emit('loadMore')
    setTimeout(() => { loadMoreCooldown.value = false }, 1000)
  }
}

// 重新加载
const reload = () => {
  trackEvent('music_list_reload_click', {
    source: props.source || 'music_list'
  })
  emit('reload')
}

// 组件挂载时设置监听器
let cleanup: (() => void) | undefined; // 声明一个变量来存储清理函数

onMounted(() => {
  // 设置播放状态监听器
  cleanup = setupPlayingStateListener()
  
  // 使用 IntersectionObserver 监听加载更多触发器
  // 仅在 autoLoadMore !== false 时设置 IntersectionObserver
  if (props.autoLoadMore !== false) {
    nextTick(() => {
      if (loadMoreTrigger.value) {
        observer.value = new IntersectionObserver((entries) => {
          if (props.isLoading || props.isLoadingMore || !props.hasMore || props.musicList.length === 0 || loadMoreCooldown.value) {
            return
          }
          const entry = entries[0]
          if (entry.isIntersecting) {
            handleLoadMore()
          }
        }, {
          root: null,
          rootMargin: '100px',
          threshold: 0.1
        })
        observer.value.observe(loadMoreTrigger.value)
      }
    })

    nextTick(() => {
      const documentHeight = document.documentElement.scrollHeight
      const windowHeight = window.innerHeight
      if (documentHeight <= windowHeight && props.hasMore && !props.isLoading && !props.isLoadingMore) {
        handleLoadMore()
      }
    })
  }
})

// 组件卸载时清理
onUnmounted(() => {
  if (cleanup) {
    cleanup()
  }
  if (observer.value) {
    observer.value.disconnect()
    observer.value = null
  }
})

// 监听props变化，重新设置IntersectionObserver（仅 autoLoadMore 模式）
watch(() => [props.musicList.length, props.hasMore], () => {
  if (props.autoLoadMore === false) return

  if (observer.value) {
    observer.value.disconnect()
    observer.value = null
  }

  nextTick(() => {
    if (loadMoreTrigger.value && props.hasMore) {
      observer.value = new IntersectionObserver((entries) => {
        if (props.isLoading || props.isLoadingMore || !props.hasMore || props.musicList.length === 0 || loadMoreCooldown.value) {
          return
        }
        const entry = entries[0]
        if (entry.isIntersecting) {
          handleLoadMore()
        }
      }, {
        root: null,
        rootMargin: '100px',
        threshold: 0.1
      })
      observer.value.observe(loadMoreTrigger.value)
    }
  })
})

// 格式化时长
const formatDuration = (seconds: number | undefined) => {
  if (!seconds) return '0:00'
  const mins = Math.floor(seconds / 60)
  const secs = Math.floor(seconds % 60)
  return `${mins}:${secs.toString().padStart(2, '0')}`
}
</script>

<style scoped>
/* 声波动画 */
.sound-wave {
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 2px;
}

.sound-wave span {
  display: block;
  width: 2px;
  height: 10px;
  background-color: white;
  border-radius: 1px;
  animation: sound-wave 1.2s infinite ease-in-out;
}

.sound-wave span:nth-child(2) {
  animation-delay: 0.2s;
}

.sound-wave span:nth-child(3) {
  animation-delay: 0.4s;
}

.sound-wave span:nth-child(4) {
  animation-delay: 0.6s;
}

.sound-wave span:nth-child(5) {
  animation-delay: 0.8s;
}

.sound-wave-bar {
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.5), transparent);
  animation: sound-wave-bar 2s infinite ease-in-out;
}

/* 移动端声波动画 */
.sound-wave-small {
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 2px;
}

.sound-wave-small span {
  display: block;
  width: 2px;
  height: 8px;
  background-color: white;
  border-radius: 1px;
  animation: sound-wave 1.2s infinite ease-in-out;
}

.sound-wave-small span:nth-child(2) {
  animation-delay: 0.2s;
}

.sound-wave-small span:nth-child(3) {
  animation-delay: 0.4s;
}

@keyframes sound-wave {
  0%, 100% {
    transform: scaleY(0.5);
  }
  50% {
    transform: scaleY(1);
  }
}

@keyframes sound-wave-bar {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(100%);
  }
}
</style>
